import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import { isEmpty, find, findIndex, padStart, findLast, partial } from 'lodash';
import {
  getRoomAvailableTasks,
  getCompletedTasks,
  getUpcomingTasks,
} from 'features/task-management/selectors';
import MaterialIcon from 'material-icons-react';
import Modal from 'react-modal';
import * as roomActions from 'features/room-builder/duck';
import * as taskActions from 'features/task-management/duck';
import * as userActions from 'core/duck';
import * as irregularityActions from 'features/irregularities/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as onlineTestingActions from 'features/online-testing/duck';
import { IrregularityModel } from 'features/irregularities';
import { RoomPolling } from 'features/task-management';
import { SessionService, ValidationService } from 'shared/services';
import { AlertMessage, Textarea, ModalInfo } from 'shared/components';
import {
  DATE_FORMATS,
  IRREGULARITIES_NOTES_MAX_LENGTH,
  ONLINE_PROVIDERS,
  ROOM_STATUS,
} from 'shared/constants';
import { convertKeywords, getPersonnelRoles } from 'shared/helpers';
import TimerSmallWatch from 'assets/svgs/timerSmallWatch.svg';
import PauseIcon from 'assets/svgs/pauseIcon24.svg';
import PlayIcon from 'assets/svgs/playIcon24.svg';

import './RoomTimer.scss';

const ROOM_TIMER = 'roomTimer';
const MODAL_Z_INDEX = 1005;
const customStyles = {
  overlay: {
    display: '-webkit-box',
    overflow: 'auto',
    zIndex: MODAL_Z_INDEX,
  },
};

const getTime = (timer) => {
  const stopTime = moment(timer.startDateTime).add(timer.startTask.timerDurationMinutes, 'minutes');

  if (timer.startTask.timerDurationSeconds && timer.startTask.timerDurationSeconds > 0) {
    stopTime.add(timer.startTask.timerDurationSeconds, 'seconds');
  }

  // add pauses to the timer
  stopTime.add(timer.pauseSeconds, 'seconds');

  if (timer.currentPauseStart && !timer.currentPauseEnd) {
    stopTime.add(
      moment.duration(
        moment().add(serverTimeDiff, 'milliseconds').diff(moment(timer.currentPauseStart))
      )
    );
  }

  // if timer has passed return null so we know to stop it
  if (moment().add(serverTimeDiff, 'milliseconds').isAfter(stopTime)) {
    return {
      countUp: moment.duration(moment().add(serverTimeDiff, 'milliseconds').diff(stopTime)),
      countDown: null,
    };
  }
  return {
    countDown: moment.duration(stopTime.diff(moment().add(serverTimeDiff, 'milliseconds'))),
    countUp: null,
  };
};

const formatTime = (duration) => {
  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  return `${padStart(hours, 2, '0')}:${padStart(minutes, 2, '0')}:${padStart(seconds, 2, '0')}`;
};

const getCount = (start) => {
  const duration = moment().add(serverTimeDiff, 'milliseconds').diff(moment(start));
  return formatTime(moment.duration(Math.max(duration, 0)));
};

const hasRole = (user, room) => {
  const { testCenterEventPersonnels } = user;
  if (!testCenterEventPersonnels?.length) {
    return false;
  }

  return testCenterEventPersonnels.some((ev) => {
    if (ev.testCenterEventId !== room.locationEvent.testCenterEventId) {
      return false;
    }
    return ev.role && ev.roomId === room.id;
  });
};

const hasHideOption = (user, room, tcCycleId) => {
  const { testCenterCyclePersonnels, testCenterEventPersonnels } = user;
  if (!testCenterEventPersonnels?.length) {
    return false;
  }
  const personnelRoles = getPersonnelRoles();

  const isTCC = testCenterCyclePersonnels.some(
    (c) => c.testCenterCycleId === tcCycleId && c.role && c.role.role === personnelRoles.COORDINATOR
  );
  return (
    isTCC ||
    testCenterEventPersonnels.some(
      (ev) =>
        ev.testCenterEventId === room.locationEvent.testCenterEventId &&
        ev.role &&
        (ev.role.role === personnelRoles.PROCTOR_ROVING ||
          ev.role.role === personnelRoles.TEST_DAY_COORDINATOR)
    )
  );
};

const shouldTimerSetup = (user, room, location) => {
  if (hasRole(user, room)) {
    return true;
  }

  //  user without an assignment, render only on room dashboard
  return (
    location.includes('test-event') &&
    location.includes('room') &&
    !location.includes('manual-timer') &&
    !location.includes('rooms')
  );
};

let timerInterval = null;
let pauseTimerInterval = null;
let serverTimeDiff = null;

class PsiRoomTimer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      errors: {},
      timer: {},
      pause: {},
      pauseDescription: '',
      pauseConfirmModalActive: false,
      warning: {},
      warningTasks: [],
      showMessageTime: 2000,
      hideWarning: {},
      taskDoneWarn: false,
      showExtraTimerInfo: false,
      showPauseModal: true,
      isAlertMessageVisible: false,
    };

    if (isEmpty(props.room)) {
      const room = SessionService.getItem(ROOM_TIMER);
      const roomId = room?.roomId;
      if (roomId) {
        this.props.actions.clearTCEventAction();
        this.props.actions.getRoomStatus(roomId);
        this.props.actions.getRoom(roomId).then(() => {
          if (!isEmpty(this.props.room)) {
            this.props.actions
              .getTcEvent(this.props.room.locationEvent.testCenterEventId)
              .then(() => {
                if (!isEmpty(this.props.tcEvent)) {
                  this.props.actions.getTasks(this.props.tcEvent.testingEvent.adminId.cycleId.id);
                }
              });
          }
        });
      }
    }

    this.clearTimer = this.clearTimer.bind(this);
    this.setPause = this.setPause.bind(this);
    this.setPauseInterval = this.setPauseInterval.bind(this);
    this.setup = this.setup.bind(this);
    this.showLastTask = this.showLastTask.bind(this);
    this.changePauseDescription = this.changePauseDescription.bind(this);
    this.setWarnings = this.setWarnings.bind(this);
    this.showWarning = this.showWarning.bind(this);
    this.onCompleteWarning = this.onCompleteWarning.bind(this);
    this.setIrreg = this.setIrreg.bind(this);
    this.removeMessage = this.removeMessage.bind(this);
    this.onDismissTaskDone = this.onDismissTaskDone.bind(this);
    this.closeWarning = this.closeWarning.bind(this);
    this.toggleExtraInfo = this.toggleExtraInfo.bind(this);
    this.toggleConfirmPause = this.toggleConfirmPause.bind(this);
    this.pauseTimer = this.pauseTimer.bind(this);
    this.onStopPause = this.onStopPause.bind(this);
  }

  componentDidMount() {
    const { user, roomStatus, room } = this.props;

    if (isEmpty(roomStatus) || isEmpty(user) || isEmpty(room) || room.loading) {
      return;
    }

    if (roomStatus.activeRoomTimerStartDateTime && roomStatus.status !== ROOM_STATUS.closed) {
      // different timer, clear the previous
      if (
        roomStatus.activeRoomTimerStartTaskId !==
          this.props.roomStatus.activeRoomTimerStartTaskId ||
        (this.props.room && room.id !== this.props.room.id)
      ) {
        this.clearTimer();
      }

      this.setup(this.props);

      return;
    }
    this.clearTimer();
  }

  componentDidUpdate(prevProps) {
    const { user, roomStatus, room } = this.props;

    if (isEmpty(roomStatus) || isEmpty(user) || isEmpty(room) || room.loading) {
      return;
    }

    // Check if props just loaded (were previously empty or loading)
    const propsJustLoaded =
      isEmpty(prevProps.roomStatus) ||
      isEmpty(prevProps.user) ||
      isEmpty(prevProps.room) ||
      prevProps.room.loading;

    if (
      roomStatus.currentServerDateTime &&
      (!serverTimeDiff ||
        (prevProps.roomStatus &&
          prevProps.roomStatus.currentServerDateTime !== roomStatus.currentServerDateTime))
    ) {
      const newDiff = moment(roomStatus.currentServerDateTime).diff(moment());
      const isChanging = Math.abs(Math.abs(newDiff) - Math.abs(serverTimeDiff)) > 1000;

      if (!serverTimeDiff || (serverTimeDiff && isChanging)) {
        serverTimeDiff = newDiff;
      }
    }

    if (roomStatus.activeRoomTimerStartDateTime && roomStatus.status !== ROOM_STATUS.closed) {
      // different timer, clear the previous
      if (
        prevProps.roomStatus &&
        (roomStatus.activeRoomTimerStartTaskId !==
          prevProps.roomStatus.activeRoomTimerStartTaskId ||
          (prevProps.room && room.id !== prevProps.room.id))
      ) {
        this.clearTimer();
      }

      // Only setup if props just loaded or if relevant props changed
      if (propsJustLoaded || prevProps.roomStatus !== roomStatus || prevProps.room !== room) {
        this.setup(this.props);
      }

      return;
    }

    // Only clear timer if it's currently active (prevent infinite loop)
    if (!isEmpty(this.state.timer) || timerInterval) {
      this.clearTimer();
    }
  }

  componentWillUnmount() {
    clearInterval(timerInterval);
    clearInterval(this.state.pauseIntervalId);
    this.clearTimer();
    if (this.poller) {
      this.poller.stop();
    }
  }

  async onCompleteWarning() {
    const { room, actions } = this.props;
    const { warning } = this.state;

    const completedTask = await actions.setCompleteTask(room.id, warning);

    if (completedTask.status === 400) {
      const roomInfo = !isEmpty(room) ? room : SessionService.getItem(ROOM_TIMER);

      actions.getRoomStatus(roomInfo.id || roomInfo.roomId);
    }
    actions.resetHideWarningAction();
    if (!this.props.location.includes('seating-chart')) {
      await actions.getRoom(room.id);
    }

    this.setState((prevState) => ({
      taskDoneWarn: !completedTask.id,
      warning: {},
      hideWarning: {},
      warningTasks: prevState.warningTasks.filter(
        (t) => t.id !== warning.id && t.displayOrder > warning.displayOrder
      ),
    }));
  }

  setPauseInterval(startTime) {
    const intervalId = setInterval(() => {
      this.setState({
        pauseTime: getCount(startTime),
        pauseIntervalId: intervalId,
      });
    }, 1000);
    pauseTimerInterval = intervalId;
  }

  setTimerInterval(timer) {
    const interval = 1000;
    const intervalId = setInterval(() => {
      this.setTimers(timer);
    }, interval);
    return intervalId;
  }

  setPause(roomStatus) {
    const { pauseTime } = this.state;

    if (roomStatus.currentPauseStartDateTime && !roomStatus.currentUnpauseStartDateTime) {
      if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
      }

      this.setState({
        pauseTime: getCount(roomStatus.currentPauseStartDateTime),
      });
      if (!pauseTimerInterval) {
        this.setPauseInterval(roomStatus.currentPauseStartDateTime);
      }
    } else {
      if (pauseTime) {
        this.setState({
          pauseTime: null,
          pauseIntervalId: null,
        });
      }
      clearInterval(pauseTimerInterval);
      pauseTimerInterval = null;
    }
  }

  setTimers(timer) {
    const diff = getTime(timer);
    if (!diff.countDown) {
      timer.time = '00:00:00';
      this.setState({
        timer,
        error: true,
      });
      this.showLastTask(timer, diff);
      return;
    }
    const count = diff.countDown;
    if (count.asSeconds() < 30) {
      this.showLastTask(timer, diff);
    } else {
      this.showWarning(count);
    }
    timer.time = formatTime(count);
    this.setState({
      timer,
      warn: count.asMinutes() < 5 && count.asMinutes() >= 1,
      error: count.asMinutes() < 1,
    });
  }

  setWarnings(timer, roomAvailableTasks, completedTasks) {
    if (!roomAvailableTasks.length) {
      return;
    }

    const startIdx = findIndex(roomAvailableTasks, (t) => t.id === timer.startTask.id);
    const endIdx = findIndex(roomAvailableTasks, (t) => t.timerAction === 'stop', startIdx);
    const warningTasks = roomAvailableTasks
      .slice(startIdx, endIdx)
      .filter(
        (task) =>
          task.timerAction === 'warning' &&
          !completedTasks.some((t) => t.taskId === task.id) &&
          completedTasks[completedTasks.length - 1].displayOrder < task.displayOrder
      );

    this.setState({
      warningTasks,
    });
  }

  closeWarning() {
    const { actions, upcomingTasks } = this.props;
    const { warning } = this.state;

    const index = upcomingTasks.findIndex((t) => t.id === warning.id);

    if (index > 0) {
      actions.setExpiredTaskWarning({
        currentTask: upcomingTasks[index - 1],
        warning: warning,
      });
    }

    this.setState({
      hideWarning: warning,
      warning: {},
    });
  }

  onDismissTaskDone() {
    this.setState({
      taskDoneWarn: false,
    });
  }

  setup(props) {
    const shouldSetup = shouldTimerSetup(props.user, props.room, props.location);
    const { roomStatus, room, actions, roomAvailableTasks, completedTasks } = props || this.props;
    if (!shouldSetup || !completedTasks.length || roomStatus.status === ROOM_STATUS.closed) {
      this.clearTimer();
      return;
    }

    Array.from(document.querySelectorAll('[data-bs-toggle="popover"]')).forEach((el) =>
      bootstrap.Popover.getOrCreateInstance(el).enable()
    );
    const startTask = roomStatus.activeRoomTimerStartTaskId
      ? roomAvailableTasks.find((task) => task.id === roomStatus.activeRoomTimerStartTaskId)
      : findLast(completedTasks, (task) => task.timerAction === 'start');

    const timer = {
      startDateTime: roomStatus.activeRoomTimerStartDateTime,
      startTask: { ...startTask },
      roomId: room.id,
      pauseSeconds: roomStatus.activeRoomTimerTotalPauseSeconds,
      currentPauseStart: roomStatus.currentPauseStartDateTime,
      currentPauseEnd: roomStatus.currentUnpauseStartDateTime,
    };

    this.setWarnings(timer, roomAvailableTasks, completedTasks);
    this.setTimers(timer);

    if (hasRole(props.user, props.room)) {
      SessionService.setItem(ROOM_TIMER, timer);
    }

    if (!timerInterval) {
      timerInterval = this.setTimerInterval(timer);
    }

    this.setPause(roomStatus);

    if (!this.poller) {
      this.poller = new RoomPolling({
        action: partial(actions.getRoomStatus, room.id),
      });
    }
  }

  showLastTask(timer, diff) {
    const { room, roomAvailableTasks } = this.props;
    const { hideWarning } = this.state;

    if (!roomAvailableTasks?.length || isEmpty(room)) {
      return;
    }

    const startIdx = roomAvailableTasks.findIndex((t) => t.id === timer.startTask.id);
    const warning = find(roomAvailableTasks, (t) => t.timerAction === 'stop', startIdx);
    warning.time = diff.countUp ? formatTime(diff.countUp) : formatTime(diff.countDown);
    warning.up = diff.countUp;

    if (!isEmpty(hideWarning)) {
      if (hideWarning.id === warning.id) {
        this.setState({
          warning: {},
        });
        this.props.actions.timerWarningAction(false);
        return;
      } else {
        this.props.actions.resetHideWarningAction();
      }
    }
    this.props.actions.timerWarningAction(true);

    this.setState({
      warning,
    });
  }

  setIrreg(pause, pauseTime) {
    const { room, user, irregularityTypes, irregularityStages } = this.props;
    const { timer } = this.state;

    let duration = 0;

    if (pauseTime) {
      const durationInitialValue = pauseTime.split(':');
      duration = Math.ceil((+durationInitialValue[0] * 60 + +durationInitialValue[1]) / 60);
    }
    const stage = irregularityStages.find((irregStage) =>
      timer.startTask.name.toLowerCase().includes(irregStage.stage)
    );
    const typeId = find(irregularityTypes, { type: 'timing_pause' }).id;
    const irregularity = new IrregularityModel({
      testCenterEventId: room.locationEvent.testCenterEventId,
      scope: 'room',
      notes: this.state.pauseDescription,
      startDateTime: moment(pause.pauseDateTime).format(DATE_FORMATS.dateTimeZone),
      duration,
      submitPersonnel: { id: user.id },
      irregularityRooms: [{ roomId: room.id }],
      irregularityType: {
        id: typeId,
      },
    });
    if (stage) {
      irregularity.irregularityStages = [{ stageId: stage.id }];
    }
    return irregularity;
  }

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  clearTimer() {
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    if (this.state.pauseIntervalId) {
      clearInterval(this.state.pauseIntervalId);
      pauseTimerInterval = null;
    }
    SessionService.removeItem(ROOM_TIMER);
    this.setState({
      timer: {},
      pauseIntervalId: null,
      warning: {},
      hideWarning: {},
    });
    this.props.actions.resetHideWarningAction();
    if (this.poller) {
      this.poller.stop();
      this.poller = null;
    }
  }

  showWarning(count) {
    const { room, actions } = this.props;
    const { timer, warningTasks, hideWarning } = this.state;

    if (isEmpty(timer) || isEmpty(room) || !warningTasks.length) {
      this.setState({
        warning: {},
      });
      actions.timerWarningAction(false);
      return;
    }

    const warnTask = findLast(warningTasks, (t) => t.timerDurationMinutes >= count.asMinutes());

    if (!warnTask || (!isEmpty(hideWarning) && hideWarning.id === warnTask.id)) {
      this.setState({
        warning: {},
      });
      actions.timerWarningAction(false);
      return;
    } else {
      if (!isEmpty(hideWarning)) {
        actions.resetHideWarningAction();
      }
    }

    this.setState({
      warning: warnTask,
    });
    this.props.actions.timerWarningAction(true);
  }

  changePauseDescription(e) {
    const { value } = e.target;
    if (value && !ValidationService.specialCharsPermissive(value)) {
      this.setState({
        errors: {
          pauseDescription: 'errors.specialCharactersNotAllowed2',
        },
      });
      return;
    }

    this.setState({
      errors: {},
    });

    this.setState({
      pauseDescription: e.target.value,
    });
  }

  toggleExtraInfo() {
    this.setState((prevState) => ({
      showExtraTimerInfo: !prevState.showExtraTimerInfo,
    }));
  }

  toggleConfirmPause() {
    const { roomStatus } = this.props;
    if (roomStatus.currentPauseId && !roomStatus.currentUnpauseStartDateTime) {
      this.setState({
        taskDoneWarn: true,
      });
      return;
    }
    this.setState((prevState) => ({
      pauseConfirmModalActive: !prevState.pauseConfirmModalActive,
    }));
  }

  async pauseTimer() {
    this.toggleConfirmPause();
    const { room, onlinePsiRegs, actions, tcEvent } = this.props;
    const isPsiEvent =
      !isEmpty(tcEvent) &&
      tcEvent?.testingEvent?.adminId &&
      tcEvent.testingEvent.adminId.onlineProvider === ONLINE_PROVIDERS.psi &&
      room.roomType?.onlineFlag;
    if (isPsiEvent && !onlinePsiRegs.length) {
      actions.getOnlinePsiRegs(room?.id);
    }

    const pause = await actions.pauseTimer(room.id);

    if (pause.id && isPsiEvent && onlinePsiRegs.length) {
      // const regIdsArr = onlinePsiRegs.map((item) => item.regId);
      // const data = { regIDs: regIdsArr };
      // const pauseResults = await OnlineTestingService.putPsiStudentStatus('pause', data);
      // Suppressed error message for failed pauseResults as requested
      // if (!pauseResults) {
      //   this.setState({ isAlertMessageVisible: true });
      // }
      actions.getOnlinePsiRegs(room?.id);
    }
    if (!pause.id) {
      this.setState({
        taskDoneWarn: true,
      });
      // force update room status to have the pause
      actions.getRoomStatus(room.id);
    }
  }

  async onStopPause() {
    const { room, actions, tcEvent, onlinePsiRegs } = this.props;
    const { pause, pauseTime } = this.state;
    const isPsiEvent =
      !isEmpty(tcEvent) &&
      tcEvent.testingEvent?.adminId &&
      tcEvent.testingEvent.adminId.onlineProvider === ONLINE_PROVIDERS.psi;
    let irreg = {
      id: null,
    };

    if (this.state.pauseDescription) {
      this.setState({
        errors: {},
      });
      const { irregularityTypes, irregularityStages } = this.props;
      if (
        !isEmpty(tcEvent) &&
        (!irregularityTypes.length ||
          !irregularityStages.length ||
          irregularityTypes[0].cycleId !== tcEvent.testingEvent.adminId.cycleId.id)
      ) {
        this.props.actions.getIrregularityStages({
          programId: tcEvent.testingEvent.adminId.cycleId.programId.id,
        });
        await this.props.actions
          .getIrregularityTypes({
            cycleId: tcEvent.testingEvent.adminId.cycleId.id,
          })
          .then(() => (irreg = this.setIrreg(pause, pauseTime)));
      } else {
        irreg = this.setIrreg(pause, pauseTime);
      }

      if (isPsiEvent && !onlinePsiRegs.length) {
        actions.getOnlinePsiRegs(room?.id);
      }

      actions.resumeTimer(room.id, irreg).then(() => {
        clearInterval(this.state.pauseIntervalId);
        pauseTimerInterval = null;
        this.setState({
          pauseIntervalId: null,
          pauseTime: null,
          pauseDescription: '',
          showMessage: true,
        });

        if (isPsiEvent) {
          actions.getOnlinePsiRegs(room?.id);
        }
      });
    } else {
      this.setState({
        errors: {
          pauseDescription: 'taskManagement.pauseTimerIrregularityRequired',
        },
      });
    }
  }

  handlePauseClick = async () => {
    const { room } = this.props;
    const pauseRoom = await this.props.actions.pauseTimer(room.id);

    if (!pauseRoom.id) {
      this.setState({ isAlertMessageVisible: true });
    }

    this.props.actions.getRoomStatus(room.id);
    const statusRequestInterval = setInterval(() => {
      if (this.props.isAutoRefreshStartedRef) this.props.isAutoRefreshStartedRef.current = true;
      this.props.setIsAutoRefreshStarted(true);
      this.props.actions.getOnlinePsiRegs(room?.id);
      this.props.setIsUpdateStatus(true);
    }, 1000);

    setTimeout(() => {
      clearInterval(statusRequestInterval);
      this.props.setIsUpdateStatus(false);
    }, 10000);
  };

  unpauseHandle = async () => {
    const { actions, tcEvent, room } = this.props;
    const { pause, pauseTime } = this.state;

    let irreg = {
      id: null,
    };

    const { irregularityTypes, irregularityStages } = this.props;
    if (
      !isEmpty(tcEvent) &&
      (!irregularityTypes.length ||
        !irregularityStages.length ||
        irregularityTypes[0].cycleId !== tcEvent.testingEvent.adminId.cycleId.id)
    ) {
      this.props.actions.getIrregularityStages({
        programId: tcEvent.testingEvent.adminId.cycleId.programId.id,
      });
      await this.props.actions
        .getIrregularityTypes({
          cycleId: tcEvent.testingEvent.adminId.cycleId.id,
        })
        .then(() => (irreg = this.setIrreg(pause, pauseTime)));
    } else {
      irreg = this.setIrreg(pause, pauseTime);
    }
    const unPauseData = await actions.resumeTimer(room.id, irreg);
    if (unPauseData && unPauseData.status !== 500) {
      clearInterval(this.state.pauseIntervalId);
      pauseTimerInterval = null;
      this.setState({
        pauseIntervalId: null,
        pauseTime: null,
        pauseDescription: '',
        showMessage: true,
      });
    } else {
      this.setState({ isAlertMessageVisible: true });
    }

    const statusRequestInterval = setInterval(() => {
      if (this.props.isAutoRefreshStartedRef) this.props.isAutoRefreshStartedRef.current = true;
      this.props.setIsAutoRefreshStarted(true);
      actions.getOnlinePsiRegs(room?.id);
      this.props.setIsUpdateStatus(true);
    }, 1000);

    setTimeout(() => {
      clearInterval(statusRequestInterval);
      this.props.setIsUpdateStatus(false);
    }, 10000);
  };

  renderSmallTimer = (endTimerTime, approximateTimer) => {
    const { timer, warn, error } = this.state;

    const width = window.innerWidth;
    const isSmallScreen = width < 1200;

    if (isSmallScreen) {
      return (
        <div className="timer-small timer-small-screens">
          <div className={`pb-2 timer ${warn ? 'warn' : ''} ${error ? 'error' : ''}`}>
            <div className="pe-2 label-time">
              <TimerSmallWatch />
            </div>
            <div className="timer-description">
              <div
                className="approx-label tooltip-link"
                data-bs-toggle="popover"
                data-bs-placement="top"
                data-bs-trigger="hover"
                data-bs-boundary="window"
                data-bs-content={this.props.t('taskManagement.approximateTimeLeftTooltip')}>
                {this.props.t('taskManagement.approximateTimeLeft')}
              </div>
              {approximateTimer && (
                <div
                  className="approx-label"
                  data-bs-toggle="popover"
                  data-bs-placement="top"
                  data-bs-trigger="hover"
                  data-bs-boundary="window"
                  data-bs-content={this.props.t('taskManagement.approximateTimeTooltip')}>
                  {this.props.t('taskManagement.approxTimeCapital')}
                </div>
              )}
              <div className="fw-bold time">{timer.time}</div>
              <div className="fw-bold text-ellipsis">
                {timer.startTask && timer.startTask.shortName}
              </div>
            </div>
          </div>
          <div className="time-values-continer">
            <div>
              <div className="fw-bold label-time">{this.props.t('common.labels.start')}</div>
              <div className="text-strong">{moment(timer.startDateTime).format('hh:mm:ss A')}</div>
            </div>
            <div>
              <div className="fw-bold label-time">{this.props.t('common.labels.end')}</div>
              <div className="text-strong">{endTimerTime.format('hh:mm:ss A')}</div>
            </div>
          </div>

          {this.state.pauseTime ? (
            <button
              onClick={() => this.toggleConfirmPause()}
              className="btn btn-action btn-primary btn-small w-100 btn-status">
              <PlayIcon />
              <span>
                {this.props.t('common.labels.pausedRoom')} {this.state.pauseTime}
              </span>
            </button>
          ) : (
            <button
              onClick={() => {
                this.setState({ pauseTime: null });
                this.toggleConfirmPause();
              }}
              className="btn btn-outline btn-small w-100 btn-status">
              <PauseIcon />
              {this.props.t('common.labels.pauseRoom')}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="timer-small">
        <div className={`pb-2 timer ${warn ? 'warn' : ''} ${error ? 'error' : ''}`}>
          <div className="pe-2 label-time">
            <TimerSmallWatch />
          </div>
          <div className="timer-description">
            <div
              className="approx-label tooltip-link"
              data-bs-toggle="popover"
              data-bs-placement="top"
              data-bs-trigger="hover"
              data-bs-boundary="window"
              data-bs-content={this.props.t('taskManagement.approximateTimeLeftTooltip')}>
              {this.props.t('taskManagement.approximateTimeLeft')}
            </div>
            {approximateTimer && (
              <div
                className="approx-label"
                data-bs-toggle="popover"
                data-bs-placement="top"
                data-bs-trigger="hover"
                data-bs-boundary="window"
                data-bs-content={this.props.t('taskManagement.approximateTimeTooltip')}>
                {this.props.t('taskManagement.approxTimeCapital')}
              </div>
            )}
            <div className="fw-bold time">{timer.time}</div>
            <div className="fw-bold text-ellipsis">
              {timer.startTask && timer.startTask.shortName}
            </div>
          </div>
        </div>
        <div>
          <div className="fw-bold label-time">{this.props.t('common.labels.start')}</div>
          <div className="text-strong">{moment(timer.startDateTime).format('hh:mm:ss A')}</div>
        </div>
        <div>
          <div className="fw-bold label-time">{this.props.t('common.labels.end')}</div>
          <div className="text-strong">{endTimerTime.format('hh:mm:ss A')}</div>
        </div>
        {this.state.pauseTime ? (
          <button
            onClick={() => this.toggleConfirmPause()}
            className="btn btn-action btn-primary btn-small w-100 btn-status">
            <PlayIcon />
            {this.props.t('common.labels.pausedRoom')}
          </button>
        ) : (
          <button
            onClick={() => {
              this.setState({ pauseTime: null });
              this.toggleConfirmPause();
            }}
            className="btn btn-outline btn-small w-100 btn-status">
            <PauseIcon />
            {this.props.t('common.labels.pauseRoom')}
          </button>
        )}
      </div>
    );
  };

  render() {
    const {
      user,
      room,
      roomStatus,
      currentEvent,
      loadingCompleteTask,
      displaySmallTimer,
      tcEvent,
    } = this.props;
    const { timer, warning, pauseTime, errors } = this.state;

    if (isEmpty(room) || room.loading || isEmpty(timer)) {
      return null;
    }

    const endTimerTime = moment(timer.startDateTime)
      .add(timer.startTask.timerDurationMinutes, 'minutes')
      .add(timer.pauseSeconds, 'seconds');

    if (timer.startTask.timerDurationSeconds && timer.startTask.timerDurationSeconds > 0) {
      endTimerTime.add(timer.startTask.timerDurationSeconds, 'seconds');
    }

    const approximateTimer =
      room.roomType.onlineFlag === true &&
      tcEvent.testingEvent?.adminId.onlineProvider === ONLINE_PROVIDERS.testNav;
    const { id } = room;

    return (
      <section
        className={`m-auto room-${id} Room-Timer-container ${
          displaySmallTimer ? 'small-container' : ''
        }`}>
        {this.renderSmallTimer(endTimerTime, approximateTimer)}
        <Modal
          isOpen={this.state.pauseConfirmModalActive}
          className="modal-info pre-pause-modal width-auto"
          shouldCloseOnOverlayClick={false}
          contentLabel="Modal"
          ariaHideApp={false}>
          <div className="modal-container p-3">
            <span className="navy-color">
              <MaterialIcon size="large" icon="pause_circle_outline" />
            </span>
            <h4 className="text-center mt-2">
              {this.props.t('taskManagement.pauseTimerConfirmationMessage')}
            </h4>
            <div>{this.props.t('taskManagement.pauseTimerConfirmationDescription')}</div>
            <div className="buttons-container w-100">
              <button className="btn btn-action w-25" onClick={this.toggleConfirmPause}>
                {this.props.t('common.labels.no')}
              </button>
              <button className="btn btn-action w-25" onClick={this.pauseTimer}>
                {this.props.t('common.labels.yes')}
              </button>
            </div>
          </div>
        </Modal>
        <Modal
          isOpen={this.state.taskDoneWarn}
          className="completed-warning-modal"
          shouldCloseOnOverlayClick={false}
          style={customStyles}
          ariaHideApp={false}>
          <div className="completed-warning-content">
            <MaterialIcon icon="error_outline" size="large" />
            <h4 className="pt-3 pb-3">{this.props.t('taskManagement.actionAlreadyCompleted')}</h4>
            <div>
              <button className="btn btn-action w-100" onClick={this.onDismissTaskDone}>
                {this.props.t('common.labels.ok')}
              </button>
            </div>
          </div>
        </Modal>
        <ModalInfo
          modalIsOpen={pauseTime ? user.id === roomStatus.currentPausePersonnelId : false}
          onSubmit={this.onStopPause}
          className="pause-timer-modal"
          headerTitle="taskManagement.pauseTimer"
          saveLabel="taskManagement.confirmAndResumeTimer"
          customContainerStyles={{
            overlay: {
              zIndex: MODAL_Z_INDEX,
            },
          }}>
          <div className="pause-timer-content">
            <p>{timer.startTask.shortName}</p>
            <p>
              {timer.time} {this.props.t('common.labels.remaining')}
            </p>
            <div className="pause-headline">
              <h4>{this.props.t('taskManagement.pausedTime')}</h4>
              <h4>{this.state.pauseTime}</h4>
            </div>
            <Textarea
              name="pauseDescription"
              label={this.props.t('taskManagement.pauseTimerDescriptionLabel')}
              value={this.state.pauseDescription}
              onChange={this.changePauseDescription}
              maxLength={IRREGULARITIES_NOTES_MAX_LENGTH}
              error={errors.pauseDescription}
            />
          </div>
        </ModalInfo>
        <AlertMessage
          show={this.state.showMessage}
          time={this.state.showMessageTime}
          removeMessage={this.removeMessage}
          message="irregularities.alertIrregularitySubmittedMessage"
        />
        <Modal
          isOpen={!isEmpty(warning)}
          className="warning-timer-modal"
          shouldCloseOnOverlayClick={false}
          style={customStyles}
          ariaHideApp={false}>
          <div className="warning-timer-modal-content">
            <MaterialIcon icon="error_outline" size="large" />
            <h4>{warning.name}</h4>
            {warning.timerAction === 'stop' && (
              <div>
                {warning.up && warning.time < '01:00' ? (
                  <h4 className="times-up">{this.props.t('taskManagement.timesUpLabel')}</h4>
                ) : (
                  <h4
                    className={
                      (!warning.up || isEmpty(warning.up)) && warning.time < '00:30' ? 'red' : ''
                    }>
                    {warning.time}
                  </h4>
                )}
              </div>
            )}

            <p
              dangerouslySetInnerHTML={{
                __html: convertKeywords(warning.instructions),
              }}
            />
            <div className="">
              <button
                className="btn btn-action w-100"
                onClick={this.onCompleteWarning}
                disabled={loadingCompleteTask}>
                {loadingCompleteTask ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  this.props.t('taskManagement.completeLabel')
                )}
              </button>
              {hasHideOption(user, room, currentEvent.testCenterCycleId) &&
                !loadingCompleteTask && (
                  <button className="btn btn-outline w-100" onClick={this.closeWarning}>
                    {this.props.t('common.labels.hide')}
                  </button>
                )}
            </div>
          </div>
        </Modal>
        <Modal
          isOpen={this.state.taskDoneWarn}
          className="completed-warning-modal"
          shouldCloseOnOverlayClick={false}
          style={customStyles}
          ariaHideApp={false}>
          <div className="completed-warning-content">
            <MaterialIcon icon="error_outline" size="large" />
            <h4 className="pt-3 pb-3">{this.props.t('taskManagement.actionAlreadyCompleted')}</h4>
            <div>
              <button className="btn btn-action w-100" onClick={this.onDismissTaskDone}>
                {this.props.t('common.labels.ok')}
              </button>
            </div>
          </div>
        </Modal>
        <AlertMessage
          show={this.state.isAlertMessageVisible}
          removeMessage={() => this.setState({ isAlertMessageVisible: false })}
          time={3000}
          message={this.props.t('errors.somethingWentWrong')}
          isError={true}
        />
      </section>
    );
  }
}

function mapStateToProps(state) {
  return {
    tcEvent: state.tcEvent.currentEvent || {},
    room: state.roomOptions.room,
    roomStatus: state.taskManagement.roomStatus,
    user: state.users.currentUser || {},
    lastCompletedTask: state.taskManagement.lastCompletedTask,
    irregularityTypes: state.statics.irregularityTypes || [],
    irregularityStages: state.statics.irregularityStages || [],
    completedTasks: getCompletedTasks(state),
    roomAvailableTasks: getRoomAvailableTasks(state),
    upcomingTasks: getUpcomingTasks(state),
    currentEvent: state.tcEvent.currentEvent,
    loadingCompleteTask: state.taskManagement.loadingCompleteTask,
    onlinePsiRegs: state.onlineTesting.onlinePsiRegs,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...roomActions,
        ...taskActions,
        ...userActions,
        ...irregularityActions,
        ...staticActions,
        ...tcEventActions,
        ...onlineTestingActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(PsiRoomTimer));

PsiRoomTimer.propTypes = {
  room: PropTypes.shape({
    id: PropTypes.number,
    roomId: PropTypes.number,
    name: PropTypes.string,
    startDateTime: PropTypes.string,
    locationEvent: PropTypes.shape({
      testCenterEventId: PropTypes.number,
    }),
  }),
  actions: PropTypes.shape({
    getRoom: PropTypes.func,
    getRoomStatus: PropTypes.func,
    getTasks: PropTypes.func,
    getIrregularityStages: PropTypes.func,
    getIrregularityTypes: PropTypes.func,
    timerWarningAction: PropTypes.func,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  roomStatus: PropTypes.shape({
    activeRoomTimerStartTaskId: PropTypes.number,
  }).isRequired,
  roomAvailableTasks: PropTypes.arrayOf(PropTypes.shape({})),
  irregularityTypes: PropTypes.arrayOf(PropTypes.shape({})),
  irregularityStages: PropTypes.arrayOf(PropTypes.shape({})),
  displaySmallTimer: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

PsiRoomTimer.defaultProps = {
  room: {},
  irregularityTypes: [],
  irregularityStages: [],
  roomAvailableTasks: [],
  displaySmallTimer: false,
};
